/**
 * 볼륨 계약의 단일 출처(single source of truth).
 *
 * 검색 워커(search.worker), OPFS sink/downloader(volume-sink, volume-writer),
 * SetupPanel, 그리고 확장의 volume-store가 모두 이 파일을 읽는다.
 *
 * OPFS 계약:
 *   navigator.storage.getDirectory()/beoptong-volumes/<fileName>
 *   쓰기 중에는 같은 디렉터리의 <fileName>.part 를 쓴다.
 *
 * SAHPool(검색 워커)은 위 파일을 `/statute-core.aknpkg`,
 * `/judgment-case-full-v4.aknpkg`로 복사해 연다. 파일명은 반드시 여기서만 정한다.
 */

/** OPFS 안의 볼륨 디렉터리. */
export const VOLUME_DIRECTORY = "beoptong-volumes";

/** 판례 tail 역인덱스 캐시(OPFS 루트). 볼륨이 아니므로 여기서 함께 소유한다. */
export const TAIL_CACHE_FILE = "beoptong-tail-index.bin";

/** 논리 키 → OPFS/SAHPool 파일명. */
export const VOLUME_FILE_NAMES = {
  "statute-core": "statute-core.aknpkg",
  "case-index": "judgment-case-full-v4.aknpkg",
  "case-bm25": "case-bm25.idx",
  "case-docptr": "case-docptr.txt",
  "case-text": "case-text.aknpkg",
} as const;

export type VolumeKey = keyof typeof VOLUME_FILE_NAMES;
export type VolumeFileName = (typeof VOLUME_FILE_NAMES)[VolumeKey];

export interface VolumeSpec {
  key: VolumeKey;
  /** hub manifest의 artifacts 키. */
  manifestKey: string;
  /** OPFS/SAHPool 파일명. */
  fileName: VolumeFileName;
  name: string;
  detail: string;
  /** 대략적인 크기(설치 전 UI 표시용). */
  sizeBytes: number;
  /** 검색 제공자에 반드시 필요한 볼륨인지. */
  required: boolean;
  /** 에어갭 파일 가져오기 버튼을 보여줄지. */
  importable: boolean;
}

export const VOLUME_SPECS: readonly VolumeSpec[] = [
  {
    key: "statute-core",
    manifestKey: "statute-core",
    fileName: VOLUME_FILE_NAMES["statute-core"],
    name: "법령",
    detail: "민법·형법·민소 등 빈출",
    sizeBytes: 75_161_600,
    required: true,
    importable: true,
  },
  {
    key: "case-index",
    manifestKey: "case-index",
    fileName: VOLUME_FILE_NAMES["case-index"],
    name: "판례 색인",
    detail: "대법원 판례 메타·인용",
    sizeBytes: 163_938_304,
    required: true,
    importable: true,
  },
  {
    key: "case-bm25",
    manifestKey: "case-bm25",
    fileName: VOLUME_FILE_NAMES["case-bm25"],
    name: "판례 검색 색인",
    detail: "BM25 키워드·관련 판례",
    sizeBytes: 195_222_134,
    required: true,
    importable: true,
  },
  {
    key: "case-docptr",
    manifestKey: "case-docptr",
    fileName: VOLUME_FILE_NAMES["case-docptr"],
    name: "판례 검색 포인터",
    detail: "BM25 문서 포인터",
    sizeBytes: 3_172_093,
    required: true,
    importable: true,
  },
  {
    key: "case-text",
    manifestKey: "case-text",
    fileName: VOLUME_FILE_NAMES["case-text"],
    name: "판례 전문",
    detail: "판례 본문 · 선택",
    sizeBytes: 1_410_895_872,
    required: false,
    importable: true,
  },
];

/**
 * 설치 범위(티어). manifest의 `tiers[].id`와 같은 값을 쓴다.
 *
 * - statutes: 조문 검색만
 * - cases:    조문 → 판례 목록/관련 판례 (기본값)
 * - full:     판례 전문 열람까지
 *
 * KURE(kure-fp16)는 티어에 넣지 않는다. 별도 선택 아티팩트다.
 */
export type TierId = "statutes" | "cases" | "full";

export interface TierSpec {
  id: TierId;
  label: string;
  /** 이 티어로 할 수 있는 일. */
  description: string;
  /** 설치할 볼륨 키. 순서 = 다운로드 순서. manifest 키와 동일하다. */
  artifacts: readonly VolumeKey[];
}

export const TIER_SPECS: readonly TierSpec[] = [
  {
    id: "statutes",
    label: "법령",
    description: "조문만 검색합니다.",
    artifacts: ["statute-core"],
  },
  {
    id: "cases",
    label: "법령 + 판례",
    description: "조문에서 판례 목록·관련 판례로 이어집니다.",
    artifacts: ["statute-core", "case-index", "case-bm25", "case-docptr"],
  },
  {
    id: "full",
    label: "법령 + 판례 + 전문",
    description: "판례 본문까지 이 기기에서 엽니다.",
    artifacts: [
      "statute-core",
      "case-index",
      "case-bm25",
      "case-docptr",
      "case-text",
    ],
  },
];

export const DEFAULT_TIER: TierId = "cases";

export function isTierId(value: unknown): value is TierId {
  return typeof value === "string" && TIER_SPECS.some((tier) => tier.id === value);
}

export function tierSpec(tier: TierId): TierSpec {
  const spec = TIER_SPECS.find((candidate) => candidate.id === tier);
  if (!spec) throw new Error(`unknown tier: ${tier}`);
  return spec;
}

/** 티어가 설치하는 볼륨 키 목록. UI와 다운로더가 같은 순서를 쓴다. */
export function artifactsForTier(tier: TierId = DEFAULT_TIER): readonly VolumeKey[] {
  return tierSpec(tier).artifacts;
}

export function volumeSpecsForTier(tier: TierId = DEFAULT_TIER): readonly VolumeSpec[] {
  return artifactsForTier(tier).map(volumeSpec);
}

/** UI 표시용 대략 크기(설치 전). manifest가 있으면 실제 size를 우선한다. */
export function tierBytes(tier: TierId = DEFAULT_TIER): number {
  return volumeSpecsForTier(tier).reduce((sum, spec) => sum + spec.sizeBytes, 0);
}

/** 설치 플래그만으로 티어 준비 여부를 판정한다. */
export function isTierReady(
  tier: TierId,
  installed: Partial<Record<VolumeKey, boolean>>,
): boolean {
  return artifactsForTier(tier).every((key) => installed[key] === true);
}

export const CORE_VOLUME_FILE = VOLUME_FILE_NAMES["statute-core"];
export const CASE_VOLUME_FILE = VOLUME_FILE_NAMES["case-index"];
export const CASE_BM25_VOLUME_FILE = VOLUME_FILE_NAMES["case-bm25"];
export const CASE_DOCPTR_VOLUME_FILE = VOLUME_FILE_NAMES["case-docptr"];
export const CASE_TEXT_VOLUME_FILE = VOLUME_FILE_NAMES["case-text"];

export const REQUIRED_VOLUME_KEYS: readonly VolumeKey[] = VOLUME_SPECS.filter(
  (spec) => spec.required,
).map((spec) => spec.key);

export function volumeSpec(key: VolumeKey): VolumeSpec {
  const spec = VOLUME_SPECS.find((candidate) => candidate.key === key);
  if (!spec) throw new Error(`unknown volume: ${key}`);
  return spec;
}

export type VolumePhase =
  | "missing"
  | "queued"
  | "downloading"
  | "paused"
  | "verifying"
  | "installed"
  | "error";

export interface VolumeState {
  key: VolumeKey;
  phase: VolumePhase;
  bytes: number;
  total: number;
  rate?: number;
  eta?: number | null;
  source?: "network" | "file";
  error?: string;
}

export type VolumeStates = Record<VolumeKey, VolumeState>;

export type VolumeEvent =
  | { type: "reset" }
  | { type: "queued"; total?: number }
  | { type: "start"; total: number; source?: "network" | "file" }
  | {
      type: "progress";
      bytes: number;
      total: number;
      rate?: number;
      eta?: number | null;
      source?: "network" | "file";
    }
  | { type: "verify"; bytes?: number; total?: number }
  | { type: "pause"; bytes?: number }
  | { type: "installed"; bytes?: number; total?: number }
  | { type: "error"; error: string; bytes?: number }
  | { type: "detect"; installed: boolean; bytes?: number };

export function initialVolumeState(key: VolumeKey): VolumeState {
  return {
    key,
    phase: "missing",
    bytes: 0,
    total: 0,
    rate: 0,
    eta: null,
  };
}

export function initialVolumeStates(): VolumeStates {
  return Object.fromEntries(
    VOLUME_SPECS.map((spec) => [spec.key, initialVolumeState(spec.key)]),
  ) as VolumeStates;
}

/** 순수 상태 머신. missing → downloading → verifying → installed. */
export function reduceVolumeState(state: VolumeState, event: VolumeEvent): VolumeState {
  switch (event.type) {
    case "reset":
      return initialVolumeState(state.key);
    case "queued":
      return {
        ...state,
        phase: "queued",
        bytes: 0,
        total: event.total ?? state.total,
        rate: 0,
        eta: null,
        error: undefined,
      };
    case "start":
      return {
        ...state,
        phase: "downloading",
        bytes: 0,
        total: event.total,
        rate: 0,
        eta: null,
        source: event.source ?? "network",
        error: undefined,
      };
    case "progress":
      return {
        ...state,
        phase: "downloading",
        bytes: event.bytes,
        total: event.total,
        rate: event.rate ?? state.rate,
        eta: event.eta ?? null,
        source: event.source ?? state.source ?? "network",
        error: undefined,
      };
    case "verify":
      return {
        ...state,
        phase: "verifying",
        bytes: event.bytes ?? state.bytes,
        total: event.total ?? state.total,
        rate: 0,
        eta: null,
      };
    case "pause":
      return {
        ...state,
        phase: "paused",
        bytes: event.bytes ?? state.bytes,
        rate: 0,
        eta: null,
      };
    case "installed": {
      const total = event.total ?? state.total;
      return {
        ...state,
        phase: "installed",
        bytes: event.bytes ?? total ?? state.bytes,
        total,
        rate: 0,
        eta: 0,
        error: undefined,
      };
    }
    case "error":
      return {
        ...state,
        phase: "error",
        bytes: event.bytes ?? state.bytes,
        rate: 0,
        eta: null,
        error: event.error,
      };
    case "detect":
      return event.installed
        ? {
            ...state,
            phase: "installed",
            bytes: event.bytes ?? state.bytes,
            total: event.bytes ?? state.total,
            rate: 0,
            eta: 0,
            error: undefined,
          }
        : initialVolumeState(state.key);
    default: {
      const never: never = event;
      return never;
    }
  }
}

export function isProviderReady(states: VolumeStates): boolean {
  return REQUIRED_VOLUME_KEYS.every((key) => states[key]?.phase === "installed");
}

/** OPFS sink의 `size(file)`만 있으면 설치 여부를 판정할 수 있다. */
export interface VolumeSizeReader {
  size(file: string): Promise<number | null>;
}

export async function detectVolumeStates(
  reader: VolumeSizeReader,
  specs: readonly VolumeSpec[] = VOLUME_SPECS,
): Promise<VolumeStates> {
  const entries = await Promise.all(
    specs.map(async (spec): Promise<[VolumeKey, VolumeState]> => {
      let size: number | null = null;
      try {
        size = await reader.size(spec.fileName);
      } catch {
        size = null;
      }
      const installed = size !== null && size > 0;
      return [
        spec.key,
        reduceVolumeState(initialVolumeState(spec.key), {
          type: "detect",
          installed,
          bytes: installed ? size ?? 0 : undefined,
        }),
      ];
    }),
  );
  return Object.fromEntries(entries) as VolumeStates;
}
